import { rgba } from 'polished'
import PropTypes from 'prop-types'
import React from 'react'

import { OnboardingLink } from 'components/common/OnboardingLink'
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'components/common/Tabs'
import Text, { styles } from 'components/common/Text'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import {
  CentOS,
  DockerImage,
  Helm,
  RedHatEL,
  UbuntuDebian,
  Windows
} from 'components/dashboard-quickstart/install-instructions'
import { downloadConfigYamlFile } from 'components/rbac-settings/teams/ConfigYamlRow'
import useAuth from 'hooks/use-auth'
import theme from 'theme'

import BannerContentWrapper from './BannerContentWrapper'

const INSTALL_TABS = [
  {
    label: 'Ubuntu, Debian',
    renderTab: <UbuntuDebian show colorTheme="light" />
  },
  {
    label: 'centOS',
    renderTab: <CentOS show colorTheme="light" />
  },
  {
    label: 'Docker Image',
    renderTab: <DockerImage show colorTheme="light" />
  },
  {
    label: 'Helm',
    renderTab: <Helm show colorTheme="light" />
  },
  {
    label: 'RHEL',
    renderTab: <RedHatEL show colorTheme="light" />
  },
  {
    label: 'Windows',
    renderTab: <Windows show colorTheme="light" />
  }
]

const NumberedHeader = ({ number, children, ...rest }) => (
  <div
    css={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      gap: theme.space[3],
      alignItems: 'center'
    }}
    {...rest}
  >
    <div
      css={{
        ...styles.h3,
        display: 'grid',
        background: theme.codeblock.dark,
        width: theme.space[6],
        height: theme.space[6],
        borderRadius: theme.borderRadius[2],
        color: theme.colors.white[0],
        placeContent: 'center'
      }}
    >
      {number}
    </div>
    <Text as="h3" color="white">
      {children}
    </Text>
  </div>
)

NumberedHeader.propTypes = {
  children: PropTypes.any.isRequired,
  number: PropTypes.string.isRequired
}

const InstallBanner = ({ showSubheader, ...rest }) => {
  const { currentTeamId, hasTeamPrivilege, RBAC } = useAuth()
  const [tabIndex, setTabIndex] = React.useState(0)
  const isLinux = INSTALL_TABS[tabIndex].label !== 'Windows'
  const isNotHelmOrDocker =
    INSTALL_TABS[tabIndex].label !== 'Docker Image' &&
    INSTALL_TABS[tabIndex].label !== 'Helm'

  return (
    <BannerContentWrapper
      css={{
        maxHeight: 'none',
        gridTemplateColumns: 'minmax(0, 1fr)',
        border: 'none'
      }}
      showContentTitle={false}
      textColor={theme.colors.white[0]}
      {...rest}
    >
      {showSubheader && (
        <Text
          as="h3"
          color="white"
          css={{
            textShadow: `0 0 5px ${rgba(
              theme.colors.black[0],
              theme.opacity[0]
            )}`,
            marginBottom: theme.space[5]
          }}
          data-testid="dashboard-install-banner-header"
        >
          <span css={{ color: theme.colors.green[0] }}>
            Start testing your infrastructure
          </span>{' '}
          against common failures and reduce your incident count.
        </Text>
      )}
      <NumberedHeader css={{ marginBottom: theme.space[3] }} number="1">
        Install Gremlin on any Linux or Windows environment.
      </NumberedHeader>
      <Tabs index={tabIndex} onChange={setTabIndex}>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            borderBottom: theme.border.base
          }}
        >
          <TabList css={{ borderBottom: 'none' }}>
            {INSTALL_TABS.map(({ label }) => (
              <Tab
                key={label}
                color={theme.colors.white[0]}
                css={{ borderBottomSize: 4 }}
                data-test-install-banner-tab={label}
              >
                {label}
              </Tab>
            ))}
          </TabList>
          <div
            css={{
              justifySelf: 'end',
              alignSelf: 'center',
              paddingBottom: theme.space[2]
            }}
          >
            <OnboardingLink
              as={TrackedExternalLink}
              css={{
                fontSize: theme.fontSizes[1]
              }}
              href="https://www.gremlin.com/docs/infrastructure-layer/installation/"
              type="contrast"
            >
              Installation Guides
            </OnboardingLink>
          </div>
        </div>

        <TabPanels>
          {INSTALL_TABS.map(({ renderTab }, index) => {
            return (
              <TabPanel
                key={`install-tab-banner-tab-${INSTALL_TABS[index].label}`}
              >
                <div
                  css={{
                    marginTop: theme.space[3]
                  }}
                >
                  {renderTab}
                </div>
              </TabPanel>
            )
          })}
        </TabPanels>
      </Tabs>
      <NumberedHeader css={{ marginTop: theme.space[3] }} number="2">
        Register the installed Gremlin with the{' '}
        <OnboardingLink
          as={TrackedExternalLink}
          href="https://www.gremlin.com/docs/quick-starts/get-started-with-gremlin-free/#register-with-gremlin-control-plane"
        >
          Gremlin Control Plane
        </OnboardingLink>{' '}
        using your Team ID and Secret Key in{' '}
        <OnboardingLink
          target="_blank"
          to={`/settings/team/${currentTeamId}/configuration`}
        >
          Team Settings
        </OnboardingLink>
        {hasTeamPrivilege(
          currentTeamId,
          RBAC.privileges.team.MANAGE_TEAM_CREDENTIALS
        ) &&
          isNotHelmOrDocker && (
            <>
              {' or download the configuration file from '}
              <a
                data-testid="config-yaml-download-link"
                download="config.yaml"
                onClick={() => downloadConfigYamlFile(currentTeamId)}
              >
                here
              </a>
              {' and copy it to '}
              <code css={{ ...styles.h3, color: theme.colors.white[0] }}>
                {isLinux
                  ? '/etc/gremlin/ '
                  : 'C:\\ProgramData\\Gremlin\\Agent '}
              </code>
            </>
          )}
        .
      </NumberedHeader>
    </BannerContentWrapper>
  )
}

InstallBanner.propTypes = {
  showSubheader: PropTypes.bool
}

export default InstallBanner
